import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Alert, Button, Dialog, IconCloseButton, Input } from '@alwindows/shared'
import { getScreenDiscount, getScreenImageSrc } from '@alwindows/shared/api/screens'
import { useQuote } from '../quote/QuoteContext'
import { ROUTE_SELECT_SCREENS, ROUTE_SUBMIT } from '../routes'
import {
  effectiveInstallCost,
  getConfigCharacteristic,
  getConfigValue,
  lineAttributes,
  lineDisplayName,
  lineTotal,
  type ScreenLine,
} from '../quote/types'

/**
 * Review step — live-site parity. Lists every configured item with its
 * customisations and per-item remove, the "please check" warning, the promo
 * code field, and the totals (Screen Total / Installation / TOTAL incl GST).
 * Customer details, comments, and terms moved to /submit, matching the live
 * flow where Summary is purely a review.
 */
/**
 * How long a removed row takes to fade + collapse before it actually leaves
 * the quote. MUST stay in step with the `.al-summary__row` transition in
 * Summary.scss — the timer is what commits the removal, so a shorter value
 * here would cut the animation off mid-way.
 */
const REMOVE_ANIMATION_MS = 280

/**
 * True when the user has asked for reduced motion. Read at click time rather
 * than subscribed to: a preference change mid-animation isn't worth the
 * listener, and `matchMedia` is guarded for older/test environments.
 */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function Summary() {
  const { quote, setQuote } = useQuote()
  const navigate = useNavigate()
  const [promoLoading, setPromoLoading] = useState(false)
  const [promoError, setPromoError] = useState<string | null>(null)
  // What's in the promo text box. A draft only — it reaches the quote (and
  // so the SAP payload) solely through a successful Apply, matching legacy
  // where the typed value stayed component-local and redux only ever held a
  // validated code.
  const [promoInput, setPromoInput] = useState(quote.promoCode)
  // Confirm-before-remove, like the legacy "Are you sure?" modal.
  const [pendingRemoval, setPendingRemoval] = useState<ScreenLine | null>(null)
  // The row currently fading + collapsing on its way out. It stays in the
  // quote (and in the totals) until the animation finishes, so the customer
  // sees WHICH row left rather than the list snapping shut under them.
  const [removingKey, setRemovingKey] = useState<string | null>(null)
  // Every fade still in flight. A set, not a single id: confirming a second
  // removal inside the 280ms window must not orphan the first one's timer.
  const removeTimers = useRef(new Set<number>())

  // A pending removal must not outlive the page — navigating away mid-fade
  // would otherwise fire setQuote on an unmounted Summary.
  useEffect(() => {
    const timers = removeTimers.current
    return () => {
      for (const id of timers) window.clearTimeout(id)
      timers.clear()
    }
  }, [])

  // The applied promo (code + discount) lives ON THE QUOTE (sessionStorage),
  // not in local or router state — /submit reads it from there, so a page
  // refresh can't send PromoCode with PromoAmount 0.
  const discount = quote.promoAmount
  const clearPromo = () => setQuote((p) => ({ ...p, promoCode: '', promoAmount: 0 }))

  const selected = quote.lines.filter((l) => l.selected && l.screenOption)
  const subtotal = selected.reduce((s, l) => s + lineTotal(l), 0)
  // Doubles for INSTALL_DOUBLE_FAM lines — keep in lockstep with the cart
  // bar on Select screens and with the Submit payload.
  const installTotal = effectiveInstallCost(quote)
  const grandTotal = subtotal + installTotal - discount
  // Legacy GST line: a ninth of the GST-inclusive screen total (after
  // savings). Installation is deliberately excluded (ScreenSummary.js).
  const gst = (subtotal - discount) / 11

  // Same header facts the Select screens page shows (legacy
  // ScreenOrderDetails) — address, frame colour, BAL.
  const firstConfig = quote.lines[0]?.sourceConfig ?? []
  const frameColour = getConfigValue(firstConfig, 'FM_ALU_COLOUR')
  const balRating = getConfigCharacteristic(firstConfig, 'BAL_RATING')
  const homeAddress = [
    quote.customer?.street,
    quote.customer?.city,
    [quote.customer?.state, quote.customer?.postcode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

  const applyPromo = async () => {
    // Sent as typed — legacy did no case-folding.
    const code = promoInput.trim()
    if (!code || !quote.customer?.salesOrg) return
    setPromoLoading(true)
    setPromoError(null)
    try {
      const rows = await getScreenDiscount(code, quote.customer.salesOrg)
      const byMaterial = new Map<string, number>()
      for (const r of rows) byMaterial.set(r.FrontEndMat, parseFloat(r.Discount) / 100)
      // Legacy parity (Helper.getDiscountTotal): EVERY window is discounted at
      // the FIRST window's material rate, and every door at the FIRST door's
      // material rate — not each line by its own material. Other product types
      // get no discount. This deliberately carries a legacy quirk (windows[0]/
      // doors[0]) so PromoAmount matches the live site on mixed-material carts.
      // Key off the first window/door in the whole order (quote.lines order),
      // as legacy did, but only sum the configured (selected) lines.
      const sumGroup = (prodType: 'WD' | 'DR') => {
        const mat = quote.lines.find((l) => l.sourceItem.ProdType === prodType)?.sourceItem.Material
        const pct = mat ? byMaterial.get(mat) : undefined
        if (!pct) return 0
        return selected
          .filter((l) => l.sourceItem.ProdType === prodType)
          .reduce((s, l) => s + lineTotal(l) * pct, 0)
      }
      // Round to 3dp exactly as legacy did before storing / sending to SAP.
      let total = sumGroup('WD') + sumGroup('DR')
      if (total > 0) total = parseFloat(total.toFixed(3))
      // Legacy kept the code even when nothing qualified (savings 0, message
      // shown); a lookup error cleared it entirely.
      setQuote((p) => ({ ...p, promoCode: code, promoAmount: total }))
      // Legacy wording, verbatim (ScreenSummary.onApply).
      if (total === 0) setPromoError('Promo code entered not applicable to your chosen items.')
    } catch (err) {
      setPromoError(err instanceof Error ? err.message : 'Promo code lookup failed.')
      clearPromo()
    } finally {
      setPromoLoading(false)
    }
  }

  const confirmRemove = () => {
    if (!pendingRemoval) return
    const targetKey = lineKey(pendingRemoval)
    setPendingRemoval(null)

    const commit = () => {
      // Only clear the flag if it's still OURS — a removal confirmed after
      // this one owns it now, and must keep fading.
      setRemovingKey((k) => (k === targetKey ? null : k))
      setQuote((prev) => ({
        ...prev,
        lines: prev.lines.map((l) => (lineKey(l) === targetKey ? { ...l, selected: false } : l)),
      }))
      // A removed item may have carried the promo discount — recompute is
      // cheap and Apply is idempotent, so un-apply (code AND amount, so SAP
      // never sees a code with a stale or zero amount) and let them re-apply.
      clearPromo()
    }

    // Nothing to wait for when the row won't animate — drop it immediately
    // rather than leaving the customer staring at an unchanged list.
    if (prefersReducedMotion()) {
      commit()
      return
    }
    setRemovingKey(targetKey)
    const id = window.setTimeout(() => {
      removeTimers.current.delete(id)
      commit()
    }, REMOVE_ANIMATION_MS)
    removeTimers.current.add(id)
  }

  if (selected.length === 0) {
    return (
      <div className="al-summary">
        <h2>Summary</h2>
        <p>You haven&rsquo;t configured any screens yet.</p>
        <Button variant="primary" asChild>
          <Link to={ROUTE_SELECT_SCREENS}>Back to select screens</Link>
        </Button>
      </div>
    )
  }

  const windows = selected.filter((l) => l.sourceItem.ProdType === 'WD')
  const doors = selected.filter((l) => l.sourceItem.ProdType === 'DR')
  const others = selected.filter(
    (l) => l.sourceItem.ProdType !== 'WD' && l.sourceItem.ProdType !== 'DR',
  )

  // Rows follow the spare-parts Cart card pattern: grey card, white image
  // tile, name block, bold price, circled-✕ remove (no qty — each row is an
  // individual opening).
  const renderSection = (title: string | null, rows: ScreenLine[]) =>
    rows.length === 0 ? null : (
      <section key={title ?? 'other'} className="al-summary__section">
        {title ? <h3 className="small bold m-bx2 no-m-t">{title}</h3> : null}
        <ul className="al-summary__items no-m no-p">
          {rows.map((line) => {
            const item = line.sourceItem
            const imgSrc = getScreenImageSrc(item.Image, item.IsCharactImage)
            const removing = lineKey(line) === removingKey
            // No `no-p` on the card — the theme helper is `padding: 0
            // !important` and would kill the card's own padding.
            return (
              <li
                key={lineKey(line)}
                className={`al-summary__row no-m ${removing ? 'is-removing' : ''}`}
              >
                <div className="al-summary__item">
                  <div className="al-summary__img">
                    {imgSrc ? <img src={imgSrc} alt="" /> : null}
                  </div>
                  <div className="al-summary__item-body">
                    <p className="no-m-t m-bx1">
                      <span className="bold">{line.roomName || lineDisplayName(line)}</span>
                      <br />
                      {lineDisplayName(line)}
                    </p>
                    {/* Every choice made for THIS opening — screen type, mesh,
                        grill, pet flap, triple lock, frame colour — labelled so
                        the customer can actually check them, as the warning
                        below asks them to. */}
                    <dl className="al-summary__attrs xsmall no-m">
                      {lineAttributes(line, frameColour).map((a) => (
                        <div key={a.label} className="al-summary__attr">
                          <dt className="bold">{a.label}</dt>
                          <dd className="no-m">{a.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                  <p className="al-summary__item-price no-m">${lineTotal(line).toFixed(2)}</p>
                  <IconCloseButton
                    className="al-summary__remove"
                    aria-label={`Remove ${line.roomName || lineDisplayName(line)}`}
                    disabled={removing}
                    onClick={() => setPendingRemoval(line)}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      </section>
    )

  return (
    <div className="al-summary">
      <h2>Summary</h2>

      {/* Home address / frame colour / BAL header (legacy ScreenOrderDetails). */}
      <div className="al-summary__details m-bx4">
        {homeAddress ? (
          <p className="no-m-t m-bx2">
            <span className="bold">Home address</span>
            <br />
            {homeAddress}
          </p>
        ) : null}
        {frameColour ? (
          <p className="no-m-t m-bx2">
            <span className="bold">Window &amp; door frame colour</span>
            <br />
            {frameColour}
          </p>
        ) : null}
        {balRating ? (
          <p className="no-m-t m-bx2">
            <span className="bold">BAL</span>
            <br />
            {balRating}
          </p>
        ) : null}
      </div>

      {renderSection('Windows', windows)}
      {renderSection('Doors', doors)}
      {renderSection(null, others)}

      {/* Legacy warning, verbatim (ScreenSummary.renderDetails). */}
      <div className="al-summary__warning flex m-tx4 m-bx4">
        <svg
          className="al-summary__warning-icon"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
        <p className="no-m">
          Please ensure you check that the windows / doors listed above and the screen details
          indicated are correct. Don&rsquo;t worry though, our team will also check for any
          potential errors before confirming your order and sending you a link to make payment.
        </p>
      </div>

      <div className="al-summary__checkout border-top">
        <div className="al-summary__promo">
          <Input
            label="Promo code"
            hideLabel
            placeholder="Promo code"
            className="input-text"
            maxLength={35}
            value={promoInput}
            onChange={(e) => {
              setPromoInput(e.target.value)
              setPromoError(null)
              // Editing the code un-applies it: SAP must only ever receive a
              // code that went through Apply, never a stale amount for a
              // different code.
              if (quote.promoCode || quote.promoAmount) clearPromo()
            }}
          />
          <Button
            variant="secondary"
            disabled={!promoInput.trim() || promoLoading}
            onClick={applyPromo}
          >
            {promoLoading ? 'Applying…' : 'Apply'}
          </Button>
        </div>
        {promoError ? <Alert variant="error">{promoError}</Alert> : null}
      </div>

      {/* Totals follow the spare-parts cart rhythm: right-aligned rows with
          paragraph spacing, bold Total, muted GST note. */}
      <section className="al-summary__totals text-right border-top">
        <p>
          Screen Total: <strong>${subtotal.toFixed(2)}</strong>
        </p>
        {discount > 0 ? (
          <p className="accent">
            You Saved: <strong>${discount.toFixed(2)}</strong>
          </p>
        ) : null}
        <p>
          Installation: <strong>${installTotal.toFixed(2)}</strong>
        </p>
        <p className="al-summary__total">
          TOTAL: <strong>${grandTotal.toFixed(2)}</strong>
        </p>
        <p className="al-summary__gst">(Incl. ${gst.toFixed(2)} GST)</p>
      </section>

      {/* Full-width row: Back hard left of the container, the red-outline
          Proceed right — same as the cart's Keep shopping / Proceed pair. */}
      <div className="al-summary__actions m-tx2">
        <Button variant="outline" asChild>
          <Link to={ROUTE_SELECT_SCREENS}>Back</Link>
        </Button>
        <Button
          variant="primary-outline"
          fill
          onClick={() => navigate(ROUTE_SUBMIT)}
        >
          Proceed
        </Button>
      </div>

      <Dialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null)
        }}
        title="Are you sure?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingRemoval(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmRemove}>
              Proceed
            </Button>
          </>
        }
      >
        <p>
          {pendingRemoval
            ? `${pendingRemoval.roomName || lineDisplayName(pendingRemoval)} will be removed from your selection. Do you wish to continue?`
            : null}
        </p>
      </Dialog>
    </div>
  )
}

function lineKey(line: ScreenLine): string {
  return `${line.sourceItem.SONo}-${line.sourceItem.ItemNo}`
}
